import React, { createContext, useContext, useReducer, useEffect } from 'react';
import authService from '../services/authService';
import { USER_TYPES } from '../constants/constants';
import apiService from '../services/api';

// Initial state
const initialState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  userType: null,
  license: null,
  branding: null,
  sessionLimitData: null,
preferences: {
  default_result_view: "table",
  results_per_page: 20,
  timezone: "Asia/Kolkata",
  display_date_format: "DD/MM/YYYY",
  view_last: "30",
  simplified_view: false,
  default_report_mode: false,
  watermark_text: "",

  report_header_fields: {
    attorney_name: true,
    email: true,
    phone: true,
    address: true,

    search_type: true,
    keyword: true,
    generated_on: true,
    jurisdiction: true,

    class: true,
    total_results: true,
    report_type: true,
    classification: true,
  },
  table_columns: {
    trademark: { status:true, type:true, classes:true, classDetails:true, state:true,
                 proprietor:true, proprietor_type:true, attorneyCode:true, doa:true,
                 dou:true, similarity:true, match:true, updated_at:true,valid_upto:true },
    attorney: { code:true, address:true, contact:true, trademarks:true, updated_at:true },
    proprietor: { proprietorType:true, address:true, state:true, trademarks:true },
}
},
  permissions: {},
  searchQuota: {
    daily: 0,
    monthly: 0,
    allowedTypes: [],
  },
  error: null,
};

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  UPDATE_BRANDING: 'UPDATE_BRANDING',
  UPDATE_LICENSE: 'UPDATE_LICENSE',
  UPDATE_QUOTA: 'UPDATE_QUOTA',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_SESSION_LIMIT: 'SET_SESSION_LIMIT',
  CLEAR_SESSION_LIMIT: 'CLEAR_SESSION_LIMIT',

};

// Helper function to calculate search quota
const calculateSearchQuota = (user, license) => {
  // console.log("user: ", user);
  if (!user) {
    return { daily: 0, monthly: 0, allowedTypes: [] };
  }

  if (user.userType === USER_TYPES.CLIENT_USER) {
    return {
      daily: user.search_quota_daily || 0,
      monthly: user.search_quota_monthly || 0,
      allowedTypes: user.search_types_allowed || [],
    };
  }

  if (user.userType === USER_TYPES.CLIENT && license) {
    return {
      daily: license.search_quota_daily || 0,
      monthly: license.search_quota_monthly || 0,
      allowedTypes: license.search_types_allowed || [],
    };
  }

  return { daily: 0, monthly: 0, allowedTypes: [] };
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ActionTypes.LOGIN_SUCCESS:
      const { user, license, branding, preferences } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        user,
        userType: user.userType,
        license,
        branding,
        preferences: preferences ?? initialState.preferences,
        permissions: user.permissions || {},
        searchQuota: calculateSearchQuota(user, license),
        error: null,
      };

    case ActionTypes.LOGIN_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        userType: null,
        license: null,
        branding: null,
        permissions: {},
        searchQuota: initialState.searchQuota,
        error: action.payload,
      };

    case ActionTypes.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      };

    case ActionTypes.UPDATE_USER:
      const updatedUser = { ...state.user, ...action.payload };
      return {
        ...state,
        user: updatedUser,
        searchQuota: calculateSearchQuota(updatedUser, state.license),
      };

    case ActionTypes.UPDATE_BRANDING:
      return {
        ...state,
        branding: { ...state.branding, ...action.payload },
      };

    case ActionTypes.UPDATE_LICENSE:
      const updatedLicense = { ...state.license, ...action.payload };
      return {
        ...state,
        license: updatedLicense,
        searchQuota: calculateSearchQuota(state.user, updatedLicense),
      };

    case ActionTypes.UPDATE_QUOTA:
      return {
        ...state,
        searchQuota: { ...state.searchQuota, ...action.payload },
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ActionTypes.SET_SESSION_LIMIT:
      return { ...state, isLoading: false, sessionLimitData: action.payload };

    case ActionTypes.CLEAR_SESSION_LIMIT:
      return { ...state, sessionLimitData: null };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext(null);

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication state
  useEffect(() => {
    initializeAuth();
  }, []);

  // Set up session timeout handler
  useEffect(() => {
    const handleSessionTimeout = () => {
      logout();
    };

    window.addEventListener('sessionTimeout', handleSessionTimeout);
    return () => {
      window.removeEventListener('sessionTimeout', handleSessionTimeout);
    };
  }, []);

  // Initialize authentication
  const initializeAuth = async () => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      if (authService.isAuthenticated()) {
        // Verify existing token
        try {
          const [userData, prefRes] = await Promise.all([
            authService.verifyToken(),
            apiService.getUserPreferences()
          ]);


          // console.log("userData initializeAuth: ", userData);
          dispatch({
            type: ActionTypes.LOGIN_SUCCESS,
            payload: {
              user: userData.user,
              license: userData.license,
              branding: userData.branding,
              preferences: prefRes?.data,
            },
          });
          authService.startSessionTimer();



        } catch (error) {
          // Token verification failed, clear auth
          await authService.logout();
          dispatch({ type: ActionTypes.LOGOUT });
        }
      } else {
        // No authentication
        dispatch({ type: ActionTypes.LOGOUT });
      }
    } catch (error) {
      // console.error('Auth initialization error:', error);
      dispatch({
        type: ActionTypes.LOGIN_FAILURE,
        payload: error.message,
      });
    }
  };

  // Login function
  //   const login = async (credentials, userType = USER_TYPES.CLIENT) => {
  //     try {
  //       dispatch({ type: ActionTypes.SET_LOADING, payload: true });
  //       dispatch({ type: ActionTypes.CLEAR_ERROR });

  //       const { email, password, rememberMe } = credentials;
  //       let response;

  //       if (userType === USER_TYPES.CLIENT) {
  //         response = await authService.clientLogin(email, password, rememberMe);
  //       } else if (userType === USER_TYPES.CLIENT_USER) {
  //         response = await authService.clientUserLogin(email, password, rememberMe);
  //       } else {
  //         throw new Error('Invalid user type');
  //       }

  //       if (response.success) { 
  //         // Extract branding - for clients it's direct, for client users it's in parentClient
  //         const branding = userType === USER_TYPES.CLIENT_USER && response.data.parentClient
  //           ? {
  //               company_name: response.data.parentClient.company_name,
  //               primary_color: response.data.parentClient.primary_color,
  //               accent_color: response.data.parentClient.accent_color,
  //               refresh_timing: response.data.parentClient.refresh_timing
  //             }
  //           : response.data.branding || null;

  //        if (userType === USER_TYPES.CLIENT) {
  //   dispatch({
  //     type: ActionTypes.LOGIN_SUCCESS,
  //     payload: {
  //       user: response.data.user,
  //       license: response.data.license,
  //       branding: response.data.branding || null,
  //     },
  //   });
  // }

  // if (userType === USER_TYPES.CLIENT_USER) {
  //   dispatch({
  //     type: ActionTypes.LOGIN_SUCCESS,
  //     payload: {
  //       user: response.data.user,        // child user
  //       license: response.data.license,  // parent license
  //       branding: branding,              // parent branding
  //     },
  //   });
  // }

  //         authService.startSessionTimer();
  //         return response;
  //       } else {
  //         throw new Error(response.message || 'Login failed');
  //       }
  //     } catch (error) {
  //       dispatch({
  //         type: ActionTypes.LOGIN_FAILURE,
  //         payload: error.message,
  //       });
  //       throw error;
  //     }
  //   };




  const login = async (credentials) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      dispatch({ type: ActionTypes.CLEAR_ERROR });

      const { email, password, rememberMe } = credentials;
      let response;
      let userType = USER_TYPES.CLIENT;

      try {

        // 🔹 Pehle client login try karo
        response = await authService.clientLogin(email, password, rememberMe);
        userType = USER_TYPES.CLIENT;

      } catch (err) {
        // console.log('[AuthContext.login] clientLogin failed:', err?.code, err?.message);

        // SESSION_LIMIT_REACHED — stop immediately, show modal
        if (err?.code === 'SESSION_LIMIT_REACHED') throw err;

        // License errors (403) — stop immediately
        const isLicenseError =
          err?.details?.code === 'LICENSE_EXPIRED'     ||
          err?.details?.code === 'LICENSE_INACTIVE'    ||
          err?.details?.code === 'LICENSE_NOT_STARTED' ||
          err?.details?.code === 'NO_LICENSE';
        if (isLicenseError) throw err;

        // Network error — clientUserLogin will also fail, don't bother
        // Check message too since our api.js throws clean errors without .response/.status
        const isNetworkError = (
          !err?.response && !err?.details && !err?.status &&
          (err?.message === 'Network Error' || err?.message?.includes('network') ||
           err?.message?.includes('timeout') || err?.message?.includes('ECONNREFUSED'))
        );
        if (isNetworkError) throw err;

        // Only fall through to clientUserLogin on a genuine credential failure
        // (email exists as client_child_user, not client)
        try {
          response = await authService.clientUserLogin(email, password, rememberMe);
          userType = USER_TYPES.CLIENT_USER;
        } catch (clientUserErr) {
          // console.log('[AuthContext.login] clientUserLogin also failed:', clientUserErr?.code, clientUserErr?.message);

          // SESSION_LIMIT_REACHED for child user
          if (clientUserErr?.code === 'SESSION_LIMIT_REACHED') throw clientUserErr;

          // Both failed — throw original client error for correct message
          throw err;
        }
      }

      if (response.success) {
        // Extract branding – client ke liye direct, client_user ke liye parentClient se
        const branding =
          userType === USER_TYPES.CLIENT_USER && response.data.parentClient
            ? {
              company_name: response.data.parentClient.company_name,
              primary_color: response.data.parentClient.primary_color,
              accent_color: response.data.parentClient.accent_color,
              refresh_timing: response.data.parentClient.refresh_timing,
            }
            : response.data.branding || null;
        // ⭐ FETCH BEFORE DISPATCH
        let preferences = initialState.preferences;

        try {
          const prefRes = await apiService.getUserPreferences();

          if (prefRes?.success) {
            preferences = prefRes.data;
          }
        } catch {
          // console.log("Pref failed — using default");
        }

        if (userType === USER_TYPES.CLIENT) {
          dispatch({
            type: ActionTypes.LOGIN_SUCCESS,
            payload: {
              user: response.data.user,
              license: response.data.license,
              branding: response.data.branding || null,
              preferences,

            },
          });
        }

        if (userType === USER_TYPES.CLIENT_USER) {
          dispatch({
            type: ActionTypes.LOGIN_SUCCESS,
            payload: {
              user: response.data.user,       // child user
              license: response.data.license, // parent license
              branding: branding,             // parent branding
              preferences,

            },
          });
        }

        authService.startSessionTimer();

        return response;
      } else {
        throw new Error(response.message || "Login failed");
      }
    } catch (error) {
      // SESSION_LIMIT_REACHED is not a login failure — it's a user choice flow.
      // Don't dispatch LOGIN_FAILURE for it; just stop the loading spinner
      // and rethrow so Login.js can show the session picker modal.
      if (error?.code === 'SESSION_LIMIT_REACHED') {
        dispatch({ type: ActionTypes.SET_SESSION_LIMIT, payload: error.details });
        return;  // Don't throw - Login reads sessionLimitData from context
      }
      dispatch({
        type: ActionTypes.LOGIN_FAILURE,
        payload: error.message,
      });
      throw error;
    }
  };

  // Logout functionAC
  const logout = async () => {
    try {
      await authService.logout();
      authService.stopSessionTimer();
      dispatch({ type: ActionTypes.LOGOUT });
    } catch (error) {
      // console.error('Logout error:', error);
      // Force logout even if server call fails
      dispatch({ type: ActionTypes.LOGOUT });
    }
  };

  // Update user profile
  const updateUser = (userData) => {
    authService.setCurrentUser({ ...state.user, ...userData });
    dispatch({
      type: ActionTypes.UPDATE_USER,
      payload: userData,
    });
  };

  // Update branding
  const updateBranding = (brandingData) => {
    const currentUser = authService.getCurrentUser();
    const updatedUser = {
      ...currentUser,
      branding: { ...currentUser.branding, ...brandingData },
    };
    authService.setCurrentUser(updatedUser);
    dispatch({
      type: ActionTypes.UPDATE_BRANDING,
      payload: brandingData,
    });
  };

  // Update license
  const updateLicense = (licenseData) => {
    const currentUser = authService.getCurrentUser();
    const updatedUser = {
      ...currentUser,
      license: { ...currentUser.license, ...licenseData },
    };
    authService.setCurrentUser(updatedUser);
    dispatch({
      type: ActionTypes.UPDATE_LICENSE,
      payload: licenseData,
    });
  };

  // Refresh authentication
  const refreshAuth = async () => {
    try {
      const [userData, prefRes] = await Promise.all([
        authService.verifyToken(),
        apiService.getUserPreferences()
      ]);


      dispatch({
        type: ActionTypes.LOGIN_SUCCESS,
        payload: {
          user: userData.user,
          // user:  userData,
          license: userData.license,
          branding: userData.branding,
          preferences: prefRes?.data || state.preferences,

        },
      });
      return userData;
    } catch (error) {
      await logout();
      throw error;
    }
  };

  // Check permission
  const hasPermission = (permission) => {
    return authService.hasPermission(permission);
  };

  // Check if search type is allowed
  const isSearchTypeAllowed = (searchType) => {
    return authService.isSearchTypeAllowed(searchType);
  };

  // Get user display name
  const getDisplayName = () => {
    if (!state.user) return '';
    return `${state.user.firstname} ${state.user.lastname}`.trim();
  };

  // Get company name
  const getCompanyName = () => {
    if (state.userType === USER_TYPES.CLIENT) {
      return state.user?.company_name || '';
    } else if (state.userType === USER_TYPES.CLIENT_USER && state.branding) {
      return state.branding.company_name || '';
    }
    return '';
  };

  // Get brand colors
 const getBrandColors = () => {
  const branding = state.branding || {};

  return {
    primary: branding.primary_color ,   // light blue
    accent: branding.accent_color ,     // dark gray
    third: branding.third_color ,       // gold
    fourth: branding.fourth_color ,     // green
  };
};
//  const getBrandColors = () => {
//   const branding = state.branding || {};

//   return {
//     primary: branding.primary_color || '#3b82f6',   // light blue
//     accent: branding.accent_color || '#6b7280',     // dark gray
//     third: branding.third_color || '#d4a017',       // gold
//     fourth: branding.fourth_color || '#2e7d32',     // green
//   };
// };

  // Clear error
  const clearError = () => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  };

  // Set error
  const setError = (error) => {
    dispatch({
      type: ActionTypes.SET_ERROR,
      payload: error,
    });
  };

  // Reset session timer on activity
  const resetSessionTimer = () => {
    authService.resetSessionTimer();
  };

  // Context value
  const value = {
    // State
    ...state,

    // Actions
    login,
    logout,
    updateUser,
    updateBranding,
    updateLicense,
    refreshAuth,

    // Utility functions
    hasPermission,
    isSearchTypeAllowed,
    getDisplayName,
    getCompanyName,
    getBrandColors,
    clearError,
    setError,
    resetSessionTimer,
    clearSessionLimit: () => dispatch({ type: ActionTypes.CLEAR_SESSION_LIMIT }),

    // Direct access to auth service methods
    changePassword: authService.changePassword.bind(authService),
    forgotPassword: authService.forgotPassword.bind(authService),
    resetPassword: authService.resetPassword.bind(authService),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;